/**
 * ReHub / 360 Tours — Active Spaces pricing model (single source of truth).
 *
 * Two ways to use the platform:
 *   1. Credits / Pay As You Go  — one-off publishing for a fixed duration.
 *   2. Active Spaces subscriptions — Standard / Sales Pro / Ultra / Enterprise.
 *
 * "Credits get your tour live. Active Spaces unlock the professional platform."
 *
 * This module is additive: the legacy `TOUR_SUBSCRIPTION_TIERS` export (pro /
 * agency / business) is kept for Planos / ImageMotion / VideoMotion which still
 * share the older shape. New 360 Tours UI + marketing should import from here.
 *
 * Backward compatibility: each paid plan carries a `legacyTier`
 * (pro/agency/business) so existing subscribers, Stripe metadata and DB rows
 * keep working. The DB maps both the new `plan_type` and the legacy
 * `tours_subscription_tier` to the same entitlements.
 */

export type ToursPlanType = 'credits' | 'standard' | 'sales_pro' | 'ultra' | 'enterprise';

/** Legacy tier stored in `profiles.tours_subscription_tier`. */
export type ToursLegacyTier = 'free' | 'pro' | 'agency' | 'business';

/** Display/checkout currency for Active Spaces plans. */
export type ToursCurrency = 'GBP' | 'EUR' | 'USD';

/** Sentinel for "unlimited" entitlements (Enterprise). */
export const TOURS_UNLIMITED = Number.POSITIVE_INFINITY;

/** Every gateable feature in the 360 Tours platform. */
export type ToursFeatureKey =
  // Standard and above
  | 'remove_rehub_branding'
  | 'saved_brands'
  | 'floor_plan_overlay'
  | 'property_information'
  | 'enhanced_hotspots'
  | 'basic_analytics'
  // Sales Pro and above
  | 'salesguide_ai'
  | 'ai_scripts'
  | 'voice_presenter'
  | 'agent_recording'
  | 'qr_recording'
  | 'multi_language'
  | 'lead_capture'
  | 'cta_buttons'
  | 'premium_viewer'
  | 'advanced_analytics'
  // Ultra and above
  | 'teams'
  | 'multi_brand_management'
  | 'white_label'
  | 'custom_domain'
  | 'presentation_mode'
  | 'priority_support'
  | 'api_access';

export interface ToursPlan {
  planType: ToursPlanType;
  /** Legacy `tours_subscription_tier` value (null for credits/enterprise). */
  legacyTier: ToursLegacyTier | null;
  name: string;
  /** Positioning / messaging-ladder line. */
  tagline: string;
  /** Monthly price in GBP. null = "Contact Sales" (Enterprise). 0 = Credits/PAYG. */
  monthlyPriceGBP: number | null;
  /** Monthly price in EUR. Proposed until Stripe EUR checkout is approved. */
  monthlyPriceEUR: number | null;
  /** Monthly price in USD. null = "Contact Sales" (Enterprise). 0 = Credits/PAYG. */
  monthlyPriceUSD: number | null;
  /** Live published properties allowed. null = unlimited. 0 = credit-only. */
  activeSpaces: number | null;
  /** Saved brand profiles allowed. null = unlimited. */
  brands: number | null;
  /** "Most Popular" badge. */
  isMostPopular?: boolean;
  /** ReHub branding shown in the viewer on this plan. */
  showRehubBranding: boolean;
  ctaLabel: string;
  /** Env var that holds the Stripe monthly price id (null = no checkout). */
  stripePriceEnv: string | null;
  /** Features introduced *at* this plan (not cumulative — see `toursPlanFeatures`). */
  planFeatures: ToursFeatureKey[];
}

const STANDARD_FEATURES: ToursFeatureKey[] = [
  'remove_rehub_branding',
  'saved_brands',
  'floor_plan_overlay',
  'property_information',
  'enhanced_hotspots',
  'basic_analytics',
];

const SALES_PRO_FEATURES: ToursFeatureKey[] = [
  'salesguide_ai',
  'ai_scripts',
  'voice_presenter',
  'agent_recording',
  'qr_recording',
  'multi_language',
  'lead_capture',
  'cta_buttons',
  'premium_viewer',
  'advanced_analytics',
];

const ULTRA_FEATURES: ToursFeatureKey[] = [
  'teams',
  'multi_brand_management',
  'white_label',
  'custom_domain',
  'presentation_mode',
  'priority_support',
  'api_access',
];

/**
 * Ordered list of plans (cheapest → most expensive). Order matters for
 * cumulative feature resolution and "next tier" / upgrade logic.
 */
export const TOURS_PLANS: ToursPlan[] = [
  {
    planType: 'credits',
    legacyTier: 'free',
    name: 'Pay As You Go',
    tagline: 'Get online.',
    monthlyPriceGBP: 0,
    monthlyPriceEUR: 0,
    monthlyPriceUSD: 0,
    activeSpaces: 0,
    brands: 1,
    showRehubBranding: true,
    ctaLabel: 'Publish with Credits',
    stripePriceEnv: null,
    planFeatures: [],
  },
  {
    planType: 'standard',
    legacyTier: 'pro',
    name: 'Standard',
    tagline: 'Make every tour look professional.',
    monthlyPriceGBP: 49,
    monthlyPriceEUR: 59,
    monthlyPriceUSD: 59,
    activeSpaces: 50,
    brands: 5,
    showRehubBranding: false,
    ctaLabel: 'Start Standard',
    stripePriceEnv: 'STRIPE_PRICE_STANDARD_MONTHLY',
    planFeatures: STANDARD_FEATURES,
  },
  {
    planType: 'sales_pro',
    legacyTier: 'agency',
    name: 'Sales Pro',
    tagline: 'Turn every tour into a 24/7 digital salesperson.',
    monthlyPriceGBP: 129,
    monthlyPriceEUR: 155,
    monthlyPriceUSD: 159,
    activeSpaces: 100,
    brands: 10,
    isMostPopular: true,
    showRehubBranding: false,
    ctaLabel: 'Start Sales Pro',
    stripePriceEnv: 'STRIPE_PRICE_SALES_PRO_MONTHLY',
    planFeatures: SALES_PRO_FEATURES,
  },
  {
    planType: 'ultra',
    legacyTier: 'business',
    name: 'Ultra',
    tagline: 'Scale your portfolio.',
    monthlyPriceGBP: 249,
    monthlyPriceEUR: 299,
    monthlyPriceUSD: 299,
    activeSpaces: 300,
    brands: 20,
    showRehubBranding: false,
    ctaLabel: 'Start Ultra',
    stripePriceEnv: 'STRIPE_PRICE_ULTRA_MONTHLY',
    planFeatures: ULTRA_FEATURES,
  },
  {
    planType: 'enterprise',
    legacyTier: null,
    name: 'Enterprise',
    tagline: 'Bespoke large portfolio support.',
    monthlyPriceGBP: null,
    monthlyPriceEUR: null,
    monthlyPriceUSD: null,
    activeSpaces: null,
    brands: null,
    showRehubBranding: false,
    ctaLabel: 'Contact Sales',
    stripePriceEnv: null,
    planFeatures: [],
  },
];

export function toursPlanMonthlyPrice(plan: ToursPlan, currency: ToursCurrency = 'GBP'): number | null {
  if (currency === 'EUR') return plan.monthlyPriceEUR;
  if (currency === 'USD') return plan.monthlyPriceUSD;
  return plan.monthlyPriceGBP;
}

export const TOURS_CREDITS_PLAN = TOURS_PLANS[0];
export const TOURS_PAID_PLANS = TOURS_PLANS.filter(
  (p) => p.planType !== 'credits',
);

/** Resolve a plan by its `plan_type`. Falls back to Credits/PAYG. */
export function getToursPlan(planType: string | null | undefined): ToursPlan {
  return TOURS_PLANS.find((p) => p.planType === planType) ?? TOURS_CREDITS_PLAN;
}

/** Resolve a plan from a legacy `tours_subscription_tier` value. */
export function getToursPlanByLegacyTier(
  tier: string | null | undefined,
): ToursPlan {
  if (!tier || tier === 'free') return TOURS_CREDITS_PLAN;
  return TOURS_PLANS.find((p) => p.legacyTier === tier) ?? TOURS_CREDITS_PLAN;
}

/**
 * Normalise either a `plan_type` or a legacy tier into a plan. Use this when a
 * profile may have one or the other set (during the migration window).
 */
export function resolveToursPlan(profile: {
  plan_type?: string | null;
  tours_subscription_tier?: string | null;
}): ToursPlan {
  if (profile.plan_type) return getToursPlan(profile.plan_type);
  return getToursPlanByLegacyTier(profile.tours_subscription_tier);
}

/** All features unlocked at or below a given plan (cumulative). */
export function toursPlanFeatures(planType: ToursPlanType): Set<ToursFeatureKey> {
  const idx = TOURS_PLANS.findIndex((p) => p.planType === planType);
  const set = new Set<ToursFeatureKey>();
  if (idx < 0) return set;
  // Enterprise inherits everything.
  if (planType === 'enterprise') {
    for (const p of TOURS_PLANS) p.planFeatures.forEach((f) => set.add(f));
    return set;
  }
  for (let i = 0; i <= idx; i++) {
    TOURS_PLANS[i].planFeatures.forEach((f) => set.add(f));
  }
  return set;
}

/** Does the given plan unlock a feature? */
export function toursPlanHasFeature(
  planType: string | null | undefined,
  feature: ToursFeatureKey,
): boolean {
  return toursPlanFeatures(getToursPlan(planType).planType).has(feature);
}

/** Active Spaces limit for a plan (Infinity = unlimited). */
export function getToursActiveSpacesLimit(
  planType: string | null | undefined,
): number {
  const plan = getToursPlan(planType);
  return plan.activeSpaces === null ? TOURS_UNLIMITED : plan.activeSpaces;
}

/** Saved-brand limit for a plan (Infinity = unlimited). */
export function getToursBrandLimit(planType: string | null | undefined): number {
  const plan = getToursPlan(planType);
  return plan.brands === null ? TOURS_UNLIMITED : plan.brands;
}

/**
 * The minimum plan that unlocks a feature, for upgrade prompts.
 * Returns the first plan in price order whose cumulative features include it.
 */
export function minimumPlanForFeature(
  feature: ToursFeatureKey,
): ToursPlan {
  for (const plan of TOURS_PLANS) {
    if (toursPlanFeatures(plan.planType).has(feature)) return plan;
  }
  // Should never happen, but default to Enterprise.
  return TOURS_PLANS[TOURS_PLANS.length - 1];
}

/** Presentation metadata for every feature (labels + locked-state copy). */
export const TOURS_FEATURE_META: Record<
  ToursFeatureKey,
  { label: string; lockedCopy: string }
> = {
  remove_rehub_branding: {
    label: 'Remove ReHub branding',
    lockedCopy: 'Upgrade to Active Spaces to remove ReHub branding.',
  },
  saved_brands: {
    label: 'Saved brand profiles',
    lockedCopy: 'Save reusable brand profiles with Standard.',
  },
  floor_plan_overlay: {
    label: 'Floor plan overlay',
    lockedCopy: 'Unlock floor plan overlays with Standard.',
  },
  property_information: {
    label: 'Property information panel',
    lockedCopy: 'Add a property information panel with Standard.',
  },
  enhanced_hotspots: {
    label: 'Enhanced hotspots',
    lockedCopy: 'Unlock enhanced hotspots with Standard.',
  },
  basic_analytics: {
    label: 'Basic analytics',
    lockedCopy: 'Get viewer analytics with Standard.',
  },
  salesguide_ai: {
    label: 'SalesGuide AI',
    lockedCopy: 'Unlock this with Sales Pro. Powered by SalesGuide AI.',
  },
  ai_scripts: {
    label: 'AI property scripts',
    lockedCopy: 'Generate AI property scripts with Sales Pro.',
  },
  voice_presenter: {
    label: 'Voice presenter',
    lockedCopy: 'Unlock the voice presenter with Sales Pro.',
  },
  agent_recording: {
    label: 'Agent recording',
    lockedCopy: 'Record your agent walkthrough with Sales Pro.',
  },
  qr_recording: {
    label: 'QR recording',
    lockedCopy: 'Unlock QR recording with Sales Pro.',
  },
  multi_language: {
    label: 'Multi-language narration',
    lockedCopy: 'Add multi-language narration with Sales Pro.',
  },
  lead_capture: {
    label: 'Lead capture forms',
    lockedCopy: 'Capture leads with Sales Pro.',
  },
  cta_buttons: {
    label: 'WhatsApp / call / enquiry buttons',
    lockedCopy: 'Add WhatsApp, call and enquiry buttons with Sales Pro.',
  },
  premium_viewer: {
    label: 'Premium viewer',
    lockedCopy: 'Unlock the premium viewer with Sales Pro.',
  },
  advanced_analytics: {
    label: 'Advanced analytics',
    lockedCopy: 'Unlock advanced analytics with Sales Pro.',
  },
  teams: {
    label: 'Team members',
    lockedCopy: 'Add team members with Ultra.',
  },
  multi_brand_management: {
    label: 'Multiple brand management',
    lockedCopy: 'Manage multiple brands with Ultra.',
  },
  white_label: {
    label: 'White label viewer',
    lockedCopy: 'White label your viewer with Ultra.',
  },
  custom_domain: {
    label: 'Custom domain',
    lockedCopy: 'Serve tours from your own domain with Ultra.',
  },
  presentation_mode: {
    label: 'Presentation mode',
    lockedCopy: 'Unlock presentation mode with Ultra.',
  },
  priority_support: {
    label: 'Priority support',
    lockedCopy: 'Get priority support with Ultra.',
  },
  api_access: {
    label: 'API access',
    lockedCopy: 'API access is available on Ultra and Enterprise.',
  },
};

// =============================================================================
// Stripe price id resolver for Active Spaces subscriptions
// =============================================================================

type ToursPriceEnv = Partial<Record<string, string>>;

export type ToursStripePriceIds = Record<
  'standard' | 'sales_pro' | 'ultra',
  string
>;

/** Env var that holds a plan's Stripe price id for a currency. */
export function toursStripePriceEnvName(
  planType: ToursPlanType,
  currency: ToursCurrency = 'GBP',
): string | null {
  const base =
    planType === 'standard'
      ? 'STRIPE_PRICE_STANDARD_MONTHLY'
      : planType === 'sales_pro'
        ? 'STRIPE_PRICE_SALES_PRO_MONTHLY'
        : planType === 'ultra'
          ? 'STRIPE_PRICE_ULTRA_MONTHLY'
          : null;
  if (!base) return null;
  // GBP keeps the original (un-suffixed) name; EUR/USD get a currency suffix.
  // EUR env vars are not set yet — checkout stays GBP/USD until approved.
  return currency === 'GBP' ? base : `${base}_${currency}`;
}

function readEnvVar(env: ToursPriceEnv, name: string): string {
  return (
    env[name] ||
    env[`VITE_${name}`] ||
    env[`NEXT_PUBLIC_${name}`] ||
    ''
  );
}

/**
 * Resolve Stripe monthly price IDs for the paid Active Spaces plans, per currency.
 *
 * Set these in Vercel project env (web apps must use the `VITE_`/`NEXT_PUBLIC_`
 * prefix) and as Supabase secrets for the checkout edge function:
 *   GBP: STRIPE_PRICE_STANDARD_MONTHLY / _SALES_PRO_ / _ULTRA_
 *   USD: STRIPE_PRICE_STANDARD_MONTHLY_USD / _SALES_PRO_..._USD / _ULTRA_..._USD
 *   EUR: STRIPE_PRICE_STANDARD_MONTHLY_EUR / … (not live until EUR prices are approved)
 *
 * Enterprise has no price — its CTA routes to the contact/demo form.
 */
export const getToursStripePriceIds = (
  env: ToursPriceEnv,
  currency: ToursCurrency = 'GBP',
): ToursStripePriceIds => ({
  standard: readEnvVar(env, toursStripePriceEnvName('standard', currency)!),
  sales_pro: readEnvVar(env, toursStripePriceEnvName('sales_pro', currency)!),
  ultra: readEnvVar(env, toursStripePriceEnvName('ultra', currency)!),
});

/** Map a paid plan_type → its Stripe price id (empty string if unconfigured). */
export function getToursStripePriceId(
  planType: ToursPlanType,
  env: ToursPriceEnv,
  currency: ToursCurrency = 'GBP',
): string {
  const ids = getToursStripePriceIds(env, currency);
  if (planType === 'standard') return ids.standard;
  if (planType === 'sales_pro') return ids.sales_pro;
  if (planType === 'ultra') return ids.ultra;
  return '';
}

/** Shared consistent marketing copy (section 11 of the spec). */
export const TOURS_COPY = {
  creditsGetLive: 'Credits get your tour live.',
  activeSpacesUnlock: 'Active Spaces unlock the professional platform.',
  oneActiveSpace: 'One Active Space = one live property experience.',
  salesProSalesperson:
    'Sales Pro turns every tour into a 24/7 digital salesperson.',
  poweredBySalesGuide: 'Powered by SalesGuide AI.',
  upgradeRemoveBranding: 'Upgrade to remove ReHub branding.',
  contactSalesMoreSpaces:
    'Need more than 300 Active Spaces? Contact Sales.',
  heroTitle: 'Simple pricing for professional 360 tours.',
  heroSubtitle:
    'Publish one tour with credits, or upgrade to Active Spaces to unlock branding, floor plans, AI sales tools and premium property marketing features.',
} as const;
